import * as luxon from 'luxon';

/**
 * A more generic interface for a parsed DateTime object.
 */
export interface ParsedDateTime {
  date: Date;
  iso: string;
  isValid: boolean;
  luxonDateTime: luxon.DateTime;
}

/**
 * Gets the current DateTime object in a specified timezone.
 * @param timeZone The IANA time zone identifier.
 * @returns A Luxon DateTime object for the current time in the specified zone.
 */
export function getNowInTimezone(timeZone: string = 'America/Los_Angeles'): luxon.DateTime {
  return luxon.DateTime.now().setZone(timeZone);
}

/**
 * Gets the start of the week (Monday) for a given DateTime in a specific timezone.
 * @param dt A Luxon DateTime object.
 * @returns A Luxon DateTime object for the start of the week.
 */
export function getStartOfWeek(dt: luxon.DateTime): luxon.DateTime {
  const startOfWeek = dt.startOf('week');
  // Debug log
  console.log('[getStartOfWeek] Input:', dt.toISODate(), dt.toFormat('ccc'), '| StartOfWeek:', startOfWeek.toISODate(), startOfWeek.toFormat('ccc'));
  return startOfWeek;
}

/**
 * Checks if two DateTime objects are on the same calendar day.
 * @param date1 The first DateTime object.
 * @param date2 The second DateTime object.
 * @returns True if they are on the same day, false otherwise.
 */
export function isSameDay(date1: luxon.DateTime, date2: luxon.DateTime): boolean {
  return date1.hasSame(date2, 'day');
}

/**
 * Formats a DateTime object to an ISO Date string (YYYY-MM-DD).
 * @param date The DateTime object to format.
 * @returns The formatted string, or an empty string if invalid.
 */
export function formatToISODate(date: luxon.DateTime): string {
  return date.toISODate() ?? '';
}

/**
 * Formats a DateTime object to the short day of the week (e.g., "Mon").
 * @param date The DateTime object to format.
 * @returns The formatted day of the week.
 */
export function formatToDayOfWeek(date: luxon.DateTime): string {
  return date.toFormat('ccc');
}

/**
 * Finds the first entry in an array that corresponds to the current day in a given timezone.
 * @param entries An array of objects.
 * @param timestampAccessor A function that returns the datetime string from an entry.
 * @param formats The format(s) to try for parsing.
 * @param timeZone The IANA time zone identifier.
 * @returns The found entry, or undefined if not found.
 */
export function findTodaysEntry<T>(
  entries: T[],
  timestampAccessor: (entry: T) => string,
  formats: string | string[],
  timeZone: string = 'America/Los_Angeles'
): T | undefined {
  const now = getNowInTimezone(timeZone);
  return entries.find(entry => {
    const timestamp = timestampAccessor(entry);
    if (!timestamp) return false;
    const parsedDate = parseDateTime(timestamp, formats, timeZone);
    return parsedDate ? isSameDay(parsedDate.luxonDateTime, now) : false;
  });
}

/**
 * Parses a datetime string using Luxon with specified formats and timezone.
 *
 * @param dateTimeStr The string to parse.
 * @param formats The format(s) to try for parsing. Can be a string or an array of strings.
 * @param timeZone The IANA time zone identifier (e.g., 'America/Los_Angeles').
 * @returns A ParsedDateTime object, or null if parsing fails.
 */
export function parseDateTime(
  dateTimeStr: string,
  formats: string | string[],
  timeZone: string = 'America/Los_Angeles'
): ParsedDateTime | null {
  if (!dateTimeStr || typeof dateTimeStr !== 'string' || dateTimeStr.trim() === '') {
    return null;
  }

  const formatsToTry = Array.isArray(formats) ? formats : [formats];
  let dt: luxon.DateTime | null = null;

  for (const format of formatsToTry) {
    const parsed = luxon.DateTime.fromFormat(dateTimeStr.trim(), format, { zone: timeZone });
    if (parsed.isValid) {
      dt = parsed;
      break;
    }
  }

  if (!dt?.isValid) {
    // As a last resort, try ISO and standard parsing
    const fallback = luxon.DateTime.fromISO(dateTimeStr.trim(), { zone: timeZone });
    if (fallback.isValid) {
      dt = fallback;
    } else {
       const jsDate = new Date(dateTimeStr.trim());
       if (!isNaN(jsDate.getTime())) {
         const fromJS = luxon.DateTime.fromJSDate(jsDate, { zone: timeZone });
         if(fromJS.isValid) {
            dt = fromJS;
         }
       }
    }
  }
  
  if (!dt || !dt.isValid) {
    console.warn(`Failed to parse datetime string "${dateTimeStr}" with provided formats.`);
    return null;
  }

  return {
    date: dt.toJSDate(),
    iso: dt.toISO() ?? '',
    isValid: true,
    luxonDateTime: dt,
  };
}

/**
 * Parses a YYYY-MM-DD string as a local date (not UTC).
 * @param dateStr The date string in YYYY-MM-DD format.
 * @returns A Date object at local midnight.
 */
export function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
} 